(function () {
  // Horizon desactiva las transiciones MPA en WebViews que pueden congelar o
  // dejar en blanco la tienda. La navegación normal sigue funcionando.
  const crossDocumentTransitionsDisabled = shouldDisableCrossDocumentViewTransitions();
  if (crossDocumentTransitionsDisabled) disableCrossDocumentViewTransitions();

  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  const renderBlockerReleaseEvents = ['pointerdown', 'keydown', 'touchstart'];
  let renderBlockerTimeout = 0;

  function releaseRenderBlocker() {
    window.clearTimeout(renderBlockerTimeout);
    renderBlockerTimeout = 0;
    renderBlockerReleaseEvents.forEach((eventName) => {
      document.removeEventListener(eventName, releaseRenderBlocker, true);
    });
    viewTransitionRenderBlocker?.remove();
  }

  // The render blocker is an enhancement aid, never a prerequisite for navigation.
  if (!viewTransitionRenderBlocker || !supportsViewTransitions() || prefersReducedMotion() || isLowPowerDevice()) {
    releaseRenderBlocker();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const elapsedTime = typeof window.performance?.now === 'function' ? window.performance.now() : 0;
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - elapsedTime);
    renderBlockerTimeout = window.setTimeout(releaseRenderBlocker, RENDER_BLOCKER_TIMEOUT_MS);

    renderBlockerReleaseEvents.forEach((eventName) => {
      document.addEventListener(eventName, releaseRenderBlocker, {
        capture: true,
        once: true,
        passive: eventName === 'touchstart',
      });
    });
  }

  const idleCallback = typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : window.setTimeout;

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      safelySkipTransition(viewTransition);
      removeStoredTransitionType();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    cancelTransitionOnInteraction(viewTransition);

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      setStoredTransitionType(transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      removeStoredTransitionType();
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      safelySkipTransition(viewTransition);
      removeStoredTransitionType();
      return;
    }

    cancelTransitionOnInteraction(viewTransition);

    const customTransitionType = getStoredTransitionType();

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      try {
        await viewTransition.finished;
      } catch (_error) {
        // A skipped transition rejects in some implementations; normal navigation already completed.
      }

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        removeStoredTransitionType();
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null | undefined} viewTransition
   * @returns {boolean}
   */
  function shouldSkipViewTransition(viewTransition) {
    return (
      !isUsableViewTransition(viewTransition) ||
      prefersReducedMotion() ||
      isLowPowerDevice() ||
      crossDocumentTransitionsDisabled
    );
  }

  /**
   * Detecta WebViews conocidas por fallar al pintar transiciones MPA.
   * Mantener sincronizado con Horizon mientras exista este workaround.
   * @param {string} [userAgent=navigator.userAgent]
   */
  function shouldDisableCrossDocumentViewTransitions(userAgent = navigator.userAgent) {
    const ua = userAgent || '';
    const androidWebView = /\bAndroid\b/i.test(ua) && /;\s?wv\)/i.test(ua);
    const knownInAppBrowser =
      /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram|musical_ly|Bytedance|BytedanceWebview|trill|TikTok)(?:\b|_)/i.test(
        ua
      );

    return androidWebView || knownInAppBrowser;
  }

  function disableCrossDocumentViewTransitions() {
    const style = document.createElement('style');
    style.setAttribute('data-view-transition-webview-opt-out', '');
    style.textContent = '@view-transition { navigation: none; }';
    (document.head || document.documentElement).appendChild(style);
    document.getElementById('view-transition-render-blocker')?.remove();
  }

  /**
   * @param {ViewTransition | null | undefined} viewTransition
   * @returns {boolean}
   */
  function isUsableViewTransition(viewTransition) {
    const ViewTransitionConstructor = window.ViewTransition;

    return (
      typeof ViewTransitionConstructor === 'function' &&
      viewTransition instanceof ViewTransitionConstructor &&
      typeof viewTransition.skipTransition === 'function' &&
      typeof viewTransition.types?.clear === 'function' &&
      typeof viewTransition.types?.add === 'function' &&
      typeof viewTransition.finished?.then === 'function'
    );
  }

  function supportsViewTransitions() {
    return typeof window.ViewTransition === 'function';
  }

  /**
   * @param {ViewTransition | null | undefined} viewTransition
   */
  function safelySkipTransition(viewTransition) {
    if (typeof viewTransition?.skipTransition !== 'function') return;

    try {
      viewTransition.skipTransition();
    } catch (_error) {
      // The transition may already be finished. Normal navigation is the fallback.
    }
  }

  /**
   * @param {ViewTransition} viewTransition
   */
  function cancelTransitionOnInteraction(viewTransition) {
    const interactionEvents = ['pointerdown', 'keydown'];
    const cancelTransition = () => safelySkipTransition(viewTransition);
    let cleanupTimeout = 0;
    const cleanup = () => {
      window.clearTimeout(cleanupTimeout);
      interactionEvents.forEach((eventName) => {
        document.removeEventListener(eventName, cancelTransition, true);
      });
    };

    interactionEvents.forEach((eventName) => {
      document.addEventListener(eventName, cancelTransition, { capture: true, once: true });
    });

    cleanupTimeout = window.setTimeout(cleanup, 3000);
    Promise.resolve(viewTransition.finished).then(cleanup, cleanup);
  }

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getStoredTransitionType() {
    try {
      return window.sessionStorage.getItem('custom-transition-type');
    } catch (_error) {
      return null;
    }
  }

  /**
   * @param {string} transitionType
   */
  function setStoredTransitionType(transitionType) {
    try {
      window.sessionStorage.setItem('custom-transition-type', transitionType);
    } catch (_error) {
      // Storage is optional; page navigation still completes without a custom type.
    }
  }

  function removeStoredTransitionType() {
    try {
      window.sessionStorage.removeItem('custom-transition-type');
    } catch (_error) {
      // Storage may be unavailable in privacy-restricted contexts.
    }
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
